import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Car, CalendarDays, Coins, ShieldCheck, BadgeCheck, Search, CheckCircle2, XCircle, AlertCircle, Power, Star } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import api, { API, formatZAR, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function AdminDashboard() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [claims, setClaims] = useState([]);
  const [pccExpiring, setPccExpiring] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (ready && (!user || user === false)) navigate("/login?next=/admin");
    if (ready && user?.id && user.role !== "admin") navigate("/dashboard");
  }, [user, ready, navigate]);

  const refresh = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data || [])).catch(() => {});
    api.get("/admin/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data || [])).catch(() => {});
    api.get("/admin/payments").then((r) => setPayments(r.data || [])).catch(() => {});
    api.get("/admin/documents", { params: { status: "pending" } }).then((r) => setDocuments(r.data || [])).catch(() => {});
    api.get("/admin/deposit-refunds").then((r) => setRefunds(r.data || [])).catch(() => {});
    api.get("/admin/payouts").then((r) => setPayouts(r.data || [])).catch(() => {});
    api.get("/admin/claims").then((r) => setClaims(r.data || [])).catch(() => {});
    api.get("/admin/drivers/pcc-expiring", { params: { days: 60 } }).then((r) => setPccExpiring(r.data || [])).catch(() => {});
  };
  const decideClaim = async (c, decision) => {
    let amount = 0;
    const cap = Math.max(0, (c.deposit || 0) - (c.late_fee || 0));
    if (decision === "approved") {
      const v = window.prompt(`Amount to approve (claimed ${formatZAR(c.amount_claimed)}, max ${formatZAR(Math.min(cap, c.amount_claimed))})`, String(Math.min(cap, c.amount_claimed)));
      if (v === null) return;
      amount = Number(v);
      if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    }
    const note = window.prompt("Note to both parties (optional)") ?? "";
    try {
      await api.post(`/admin/claims/${c.id}/decide`, { decision, amount_approved: amount, note });
      refresh();
      toast.success(decision === "approved" ? "Claim approved — deposit adjusted, host credited" : "Claim rejected — full refund queued");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not decide claim");
    }
  };
  const markPayoutPaid = async (row) => {
    const reference = window.prompt(`EFT reference for ${row.owner_name} — ${formatZAR(row.amount)} (cycle ${row.cycle_date})`) ?? "";
    try {
      await api.post("/admin/payouts/mark-paid", { owner_id: row.owner_id, cycle_date: row.cycle_date, reference });
      refresh();
      toast.success("Payout recorded — host notified");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not record payout");
    }
  };
  const markRefund = async (id, refund_status) => {
    const reference = refund_status === "processed" ? window.prompt("PayFast / bank refund reference (optional)") ?? "" : "";
    try {
      await api.patch(`/admin/deposit-refunds/${id}`, { refund_status, reference });
      refresh();
      toast.success(refund_status === "processed" ? "Refund marked as processed — customer notified" : "Refund reopened");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };
  useEffect(() => { if (user?.id && user.role === "admin") refresh(); }, [user?.id, user?.role]);

  if (user?.role !== "admin") return null;

  const filteredUsers = q ? users.filter((u) => (u.email + " " + u.name).toLowerCase().includes(q.toLowerCase())) : users;

  const verifyDoc = async (docId, status) => {
    try {
      await api.patch(`/admin/documents/${docId}`, { status });
      refresh();
      toast.success(`Document ${status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    }
  };
  const toggleVehicle = async (id, key, value) => {
    try {
      await api.patch(`/admin/vehicles/${id}`, { [key]: value });
      refresh();
      toast.success("Vehicle updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };
  const toggleVerified = async (id, key, value) => {
    try {
      await api.patch(`/admin/users/${id}`, { [key]: value });
      refresh();
      toast.success("User updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">Admin</p>
        <h1 className="font-brand text-3xl sm:text-4xl text-[#0a1f4d] mt-2">Operations dashboard</h1>

        {stats && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Users" value={stats.users} icon={<Users size={18}/>} />
            <Stat label="Vehicles" value={stats.vehicles} icon={<Car size={18}/>} />
            <Stat label="Bookings" value={stats.bookings} icon={<CalendarDays size={18}/>} />
            <Stat label="Confirmed" value={stats.confirmed} icon={<Star size={18}/>} />
            <Stat label="Revenue (ZAR)" value={formatZAR(stats.revenue_zar)} icon={<Coins size={18}/>} />
          </div>
        )}

        <div className="mt-8 border-b border-[#E2E8F0] flex gap-5 overflow-x-auto no-scrollbar">
          {[
            { key: "overview", label: "Overview" },
            { key: "users", label: `Users (${users.length})` },
            { key: "vehicles", label: `Vehicles (${vehicles.length})` },
            { key: "bookings", label: `Bookings (${bookings.length})` },
            { key: "documents", label: `Pending docs (${documents.length})` },
            { key: "payments", label: `Payments (${payments.length})` },
            { key: "refunds", label: `Deposit refunds (${refunds.filter((r) => r.refund_status !== "processed").length})` },
            { key: "payouts", label: `Host payouts (${payouts.filter((p) => p.status !== "paid").length})` },
            { key: "claims", label: `Damage claims (${claims.filter((c) => !["approved", "rejected"].includes(c.status)).length})` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`pb-3 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
                tab === t.key ? "border-[#1d7ffd] text-[#0a1f4d]" : "border-transparent text-[#64748B] hover:text-[#0a1f4d]"
              }`}
              data-testid={`admin-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Panel title="Pending verifications">
              {documents.length === 0 ? <Empty msg="No pending document reviews." /> : documents.slice(0, 5).map((d) => (
                <div key={d.id} className="flex items-center gap-3 p-3 rounded-xl border border-[#E2E8F0] mb-2">
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#F0F2F5] flex-shrink-0">
                    <img src={`${API}/files/${d.file_id}`} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0a1f4d] truncate">{d.user?.name} <span className="text-[10px] uppercase font-bold text-[#64748B]">· {d.doc_type === "police_clearance" ? "police clearance" : d.doc_type}</span></p>
                    <p className="text-xs text-[#64748B] truncate">{d.user?.email}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => verifyDoc(d.id, "approved")} className="p-2 rounded-full bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25" aria-label="Approve" data-testid={`doc-approve-${d.id}`}><CheckCircle2 size={14}/></button>
                    <button onClick={() => verifyDoc(d.id, "rejected")} className="p-2 rounded-full bg-[#EF4444]/15 text-[#EF4444] hover:bg-[#EF4444]/25" aria-label="Reject"><XCircle size={14}/></button>
                  </div>
                </div>
              ))}
            </Panel>
            <Panel title="Police clearances expiring (60 days)">
              {pccExpiring.length === 0 ? <Empty msg="No chauffeur certificates expiring soon." /> : pccExpiring.map((d) => (
                <div key={d.id} className="flex items-center gap-3 p-3 rounded-xl border border-[#E2E8F0] mb-2" data-testid={`pcc-expiring-${d.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0a1f4d] truncate">{d.name} <span className="text-[10px] text-[#64748B]">· {d.email}</span></p>
                    <p className="text-xs text-[#64748B]">Expires {d.expires_on} · reminders sent: {d.reminders_sent.length ? d.reminders_sent.map((s) => (s === 0 ? "expiry" : `${s}d`)).join(", ") : "none yet"}{d.renewal_pending ? " · renewal awaiting review" : ""}</p>
                  </div>
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${d.days_left < 0 ? "bg-[#EF4444]/15 text-[#B91C1C]" : d.days_left <= 7 ? "bg-[#F59E0B]/15 text-[#B45309]" : "bg-[#F4F8FF] text-[#0a1f4d]"}`}>
                    {d.days_left < 0 ? `expired ${-d.days_left}d ago` : d.days_left === 0 ? "expires today" : `${d.days_left}d left`}
                  </span>
                </div>
              ))}
            </Panel>
            <Panel title="Recent bookings">
              {bookings.length === 0 ? <Empty msg="No bookings yet." /> : bookings.slice(0, 5).map((b) => (
                <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl border border-[#E2E8F0] mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0a1f4d] truncate">{b.vehicle_title}</p>
                    <p className="text-xs text-[#64748B]">{b.customer_name} · {format(new Date(b.start_date), "d MMM")} – {format(new Date(b.end_date), "d MMM")}</p>
                  </div>
                  <StatusPill status={b.status} />
                  <span className="font-display font-black text-[#0a1f4d] text-sm">{formatZAR(b.pricing?.total_due)}</span>
                </div>
              ))}
            </Panel>
          </div>
        )}

        {tab === "users" && (
          <div className="mt-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name" className="w-full pl-9 pr-3 py-2.5 rounded-full border border-[#E2E8F0] text-sm bg-white" data-testid="admin-user-search" />
              </div>
            </div>
            <Table headers={["Name", "Email", "Role", "ID", "Licence", "PCC", "Actions"]}>
              {filteredUsers.map((u) => (
                <tr key={u.id} className="border-t border-[#E2E8F0]" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-4 py-3 text-sm font-semibold text-[#0a1f4d]">{u.name}</td>
                  <td className="px-4 py-3 text-sm text-[#334155]">{u.email}</td>
                  <td className="px-4 py-3 text-xs"><span className="px-2 py-0.5 rounded-md bg-[#F0F2F5] uppercase font-bold tracking-wide text-[#0a1f4d]">{u.role}</span></td>
                  <td className="px-4 py-3 text-xs">{u.id_verified ? <Pill ok>Verified</Pill> : <Pill>Pending</Pill>}</td>
                  <td className="px-4 py-3 text-xs">{u.licence_verified ? <Pill ok>Verified</Pill> : <Pill>Pending</Pill>}</td>
                  <td className="px-4 py-3 text-xs">{u.role === "driver" ? (u.police_clearance_verified ? <Pill ok>Verified</Pill> : <Pill>Pending</Pill>) : <span className="text-[#94A3B8]">—</span>}</td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex gap-2">
                      <button onClick={() => toggleVerified(u.id, "id_verified", !u.id_verified)} className="px-2 py-1 rounded-md border border-[#E2E8F0] hover:bg-[#F7F8FA] font-semibold" data-testid={`toggle-id-${u.id}`}>Toggle ID</button>
                      <button onClick={() => toggleVerified(u.id, "licence_verified", !u.licence_verified)} className="px-2 py-1 rounded-md border border-[#E2E8F0] hover:bg-[#F7F8FA] font-semibold">Toggle Licence</button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "vehicles" && (
          <div className="mt-6">
            <Table headers={["Car", "Owner", "City", "R/day", "Active", "Certified", "Actions"]}>
              {vehicles.map((v) => (
                <tr key={v.id} className="border-t border-[#E2E8F0]" data-testid={`admin-vehicle-row-${v.id}`}>
                  <td className="px-4 py-3 text-sm font-semibold text-[#0a1f4d] truncate max-w-xs">{v.brand} {v.model}</td>
                  <td className="px-4 py-3 text-sm text-[#334155]">{v.owner_name}</td>
                  <td className="px-4 py-3 text-sm text-[#334155]">{v.city}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-[#0a1f4d]">{formatZAR(v.price_per_day)}</td>
                  <td className="px-4 py-3 text-xs">{v.is_active ? <Pill ok>Live</Pill> : <Pill>Paused</Pill>}</td>
                  <td className="px-4 py-3 text-xs">{v.inspection_certified ? <Pill ok>Yes</Pill> : <Pill>No</Pill>}</td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex gap-2">
                      <button onClick={() => toggleVehicle(v.id, "is_active", !v.is_active)} className="px-2 py-1 rounded-md border border-[#E2E8F0] hover:bg-[#F7F8FA] font-semibold inline-flex items-center gap-1"><Power size={12}/> {v.is_active ? "Pause" : "Activate"}</button>
                      <button onClick={() => toggleVehicle(v.id, "inspection_certified", !v.inspection_certified)} className="px-2 py-1 rounded-md border border-[#E2E8F0] hover:bg-[#F7F8FA] font-semibold inline-flex items-center gap-1"><BadgeCheck size={12}/> Certify</button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "bookings" && (
          <div className="mt-6">
            <Table headers={["Customer", "Vehicle", "Dates", "Total", "Status"]}>
              {bookings.map((b) => (
                <tr key={b.id} className="border-t border-[#E2E8F0]">
                  <td className="px-4 py-3 text-sm font-semibold text-[#0a1f4d]">{b.customer_name}</td>
                  <td className="px-4 py-3 text-sm text-[#334155]">{b.vehicle_title}</td>
                  <td className="px-4 py-3 text-xs text-[#64748B]">{format(new Date(b.start_date), "d MMM")} – {format(new Date(b.end_date), "d MMM yyyy")}</td>
                  <td className="px-4 py-3 text-sm font-bold text-[#0a1f4d]">{formatZAR(b.pricing?.total_due)}</td>
                  <td className="px-4 py-3"><StatusPill status={b.status} /></td>
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "documents" && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.length === 0 && <Empty msg="No pending document reviews." />}
            {documents.map((d) => (
              <div key={d.id} className="rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid={`admin-doc-${d.id}`}>
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[#F0F2F5] mb-3">
                  <img src={`${API}/files/${d.file_id}`} alt="" className="w-full h-full object-cover" />
                </div>
                <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B]">{d.doc_type === "id" ? "South African ID" : d.doc_type === "police_clearance" ? `Police clearance certificate${d.issued_on ? ` · issued ${d.issued_on} · valid to ${d.expires_on}` : ""}` : "Driver's licence"}</p>
                <p className="font-display font-bold text-[#0a1f4d] mt-1">{d.user?.name}</p>
                <p className="text-xs text-[#64748B]">{d.user?.email}</p>
                <p className="text-xs text-[#64748B] mt-1">Submitted {format(new Date(d.created_at), "d MMM, HH:mm")}</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => verifyDoc(d.id, "approved")} className="flex-1 bg-[#10B981] text-white text-xs font-bold py-2 rounded-full hover:bg-[#0d9d70] inline-flex items-center justify-center gap-1"><CheckCircle2 size={12}/> Approve</button>
                  <button onClick={() => verifyDoc(d.id, "rejected")} className="flex-1 bg-white border border-[#EF4444] text-[#EF4444] text-xs font-bold py-2 rounded-full hover:bg-[#EF4444] hover:text-white inline-flex items-center justify-center gap-1"><XCircle size={12}/> Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "payments" && (
          <div className="mt-6">
            <Table headers={["Provider", "Reference", "Amount", "Currency", "Status", "Created"]}>
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-[#E2E8F0]">
                  <td className="px-4 py-3 text-xs font-bold uppercase text-[#0a1f4d]">{p.provider || "stripe"}</td>
                  <td className="px-4 py-3 text-xs text-[#334155] truncate max-w-[200px]">{p.pf_payment_id || p.session_id}</td>
                  <td className="px-4 py-3 text-sm font-bold text-[#0a1f4d]">{formatZAR(p.amount)}</td>
                  <td className="px-4 py-3 text-xs uppercase font-bold text-[#64748B]">{p.currency}</td>
                  <td className="px-4 py-3 text-xs"><Pill ok={p.payment_status === "paid"}>{p.payment_status}</Pill></td>
                  <td className="px-4 py-3 text-xs text-[#64748B]">{format(new Date(p.created_at), "d MMM yyyy, HH:mm")}</td>
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "payouts" && (
          <div className="mt-6" data-testid="admin-payouts">
            <div className="mb-4 rounded-2xl border border-[#1d7ffd]/30 bg-[#1d7ffd]/5 p-4 text-sm text-[#0a1f4d]">
              <p className="font-bold">Weekly host payouts (Fridays).</p>
              <p className="mt-0.5 text-[#334155]">Trips completed Sat–Fri are batched per host. Make the EFT from your bank using the details shown, then mark the batch paid — the host is notified with your reference.</p>
            </div>
            <Table headers={["Cycle", "Host", "Bank details", "Trips", "Amount", "Status", ""]}>
              {payouts.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-[#64748B]">No completed trips awaiting payout.</td></tr>
              )}
              {payouts.map((p) => (
                <tr key={`${p.owner_id}-${p.cycle_date}-${p.status}`} className="border-t border-[#E2E8F0]" data-testid={`payout-row-${p.owner_id}-${p.cycle_date}-${p.status}`}>
                  <td className="px-4 py-3 text-sm font-semibold text-[#0a1f4d] whitespace-nowrap">Fri {format(new Date(p.cycle_date), "d MMM yyyy")}</td>
                  <td className="px-4 py-3 text-sm text-[#0a1f4d]">{p.owner_name}<div className="text-[11px] text-[#64748B]">{p.owner_email}</div></td>
                  <td className="px-4 py-3 text-xs text-[#334155]">
                    {p.bank_details ? (
                      <div data-testid="payout-bank-details">
                        <div className="font-semibold text-[#0a1f4d]">{p.bank_details.bank_name} · {p.bank_details.account_holder}</div>
                        <div>Acc {p.bank_details.account_number} · Branch {p.bank_details.branch_code} · {p.bank_details.account_type}</div>
                      </div>
                    ) : <span className="text-[#B45309] font-semibold">No bank details yet</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#334155]">{p.booking_count}</td>
                  <td className="px-4 py-3 text-sm font-bold text-[#0a1f4d]">{formatZAR(p.amount)}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${p.status === "paid" ? "bg-[#10B981]/15 text-[#065F46]" : p.status === "due" ? "bg-[#F59E0B]/15 text-[#B45309]" : "bg-[#1d7ffd]/10 text-[#1d7ffd]"}`}>{p.status}</span>
                    {p.reference && <div className="text-[11px] text-[#64748B] mt-1">ref {p.reference}</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {p.status !== "paid" && (
                      <button onClick={() => markPayoutPaid(p)} disabled={!p.bank_details} title={!p.bank_details ? "Host has not added bank details" : ""} className="text-xs font-bold text-white bg-[#0a1f4d] rounded-full px-3 py-1.5 disabled:opacity-40" data-testid={`payout-mark-paid-${p.owner_id}-${p.cycle_date}`}>Mark paid</button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "claims" && (
          <div className="mt-6 space-y-4" data-testid="admin-claims">
            <div className="rounded-2xl border border-[#F59E0B]/40 bg-[#FFFBEB] p-4 text-sm text-[#78350F]">
              <p className="font-bold">Settle damage claims against the guest's deposit.</p>
              <p className="mt-0.5">Compare the host's claim and photos with the pickup handover photos and the guest's response. Approve a full or partial amount (capped at deposit − late fee) or reject. The guest refund and the host's next payout are adjusted automatically and both parties are notified.</p>
            </div>
            {claims.length === 0 && <p className="text-sm text-[#64748B] py-6 text-center">No damage claims.</p>}
            {claims.map((c) => {
              const decided = ["approved", "rejected"].includes(c.status);
              const cap = Math.max(0, (c.deposit || 0) - (c.late_fee || 0));
              return (
                <div key={c.id} className="rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid={`claim-row-${c.id}`}>
                  <div className="flex flex-wrap items-start gap-4">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-display font-bold text-[#0a1f4d]">{c.vehicle_title}</p>
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${c.status === "approved" ? "bg-[#10B981]/15 text-[#065F46]" : c.status === "rejected" ? "bg-[#EF4444]/15 text-[#B91C1C]" : c.status === "under_review" ? "bg-[#1d7ffd]/10 text-[#1d7ffd]" : "bg-[#F59E0B]/15 text-[#B45309]"}`}>{c.status.replace("_", " ")}</span>
                      </div>
                      <p className="text-xs text-[#64748B] mt-0.5">Host {c.owner_name} → Guest {c.customer_name} · opened {format(new Date(c.created_at), "d MMM, HH:mm")}</p>
                      <p className="text-sm text-[#334155] mt-3 whitespace-pre-line">{c.description}</p>
                      {c.photos?.length > 0 && (
                        <div className="flex gap-2 flex-wrap mt-2">
                          {c.photos.map((p, i) => <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} target="_blank" rel="noreferrer"><img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" className="w-16 h-16 rounded-lg object-cover border border-[#E2E8F0]" /><p className="text-[9px] text-center text-[#64748B] capitalize">{p.phase}</p></a>)}
                        </div>
                      )}
                      <div className="mt-3 rounded-xl bg-[#F7F8FA] border border-[#E2E8F0] p-3">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-[#0a1f4d]">Guest response</p>
                        {c.guest_response ? (
                          <>
                            <p className="text-sm text-[#334155] mt-1 whitespace-pre-line">{c.guest_response.text}</p>
                            {c.guest_response.photos?.length > 0 && <div className="flex gap-2 mt-2">{c.guest_response.photos.map((p, i) => <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} target="_blank" rel="noreferrer"><img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" className="w-14 h-14 rounded-lg object-cover border border-[#E2E8F0]" /></a>)}</div>}
                          </>
                        ) : <p className="text-sm text-[#64748B] mt-1">No response yet (deadline {format(new Date(c.respond_by), "d MMM, HH:mm")}).</p>}
                      </div>
                      {c.handover_photos?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-[11px] font-bold uppercase tracking-wide text-[#64748B]">All handover photos</p>
                          <div className="flex gap-2 flex-wrap mt-1">{c.handover_photos.map((p) => <a key={p.id} href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} target="_blank" rel="noreferrer"><img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" className="w-12 h-12 rounded-lg object-cover border border-[#E2E8F0]" /><p className="text-[9px] text-center text-[#64748B] capitalize">{p.phase} · {p.by_role}</p></a>)}</div>
                        </div>
                      )}
                    </div>
                    <div className="w-full sm:w-56 rounded-xl border border-[#E2E8F0] p-4 text-sm space-y-1.5">
                      <div className="flex justify-between"><span className="text-[#64748B]">Claimed</span><span className="font-bold text-[#0a1f4d]">{formatZAR(c.amount_claimed)}</span></div>
                      <div className="flex justify-between"><span className="text-[#64748B]">Deposit</span><span className="text-[#0a1f4d]">{formatZAR(c.deposit)}</span></div>
                      {c.late_fee > 0 && <div className="flex justify-between"><span className="text-[#64748B]">Late fee</span><span className="text-[#B45309]">-{formatZAR(c.late_fee)}</span></div>}
                      <div className="flex justify-between border-t border-[#EEF2F7] pt-1.5"><span className="text-[#64748B]">Max approvable</span><span className="font-bold text-[#0a1f4d]">{formatZAR(Math.min(cap, c.amount_claimed))}</span></div>
                      {c.decision && (
                        <div className="pt-2 text-xs text-[#334155]">
                          <p className="font-bold text-[#0a1f4d]">{c.status === "approved" ? `Approved ${formatZAR(c.decision.amount_approved)}` : "Rejected"}</p>
                          <p>Guest refund {formatZAR(c.decision.guest_refund)} · by {c.decision.decided_by_name}</p>
                          {c.decision.note && <p className="italic mt-1">“{c.decision.note}”</p>}
                        </div>
                      )}
                      {!decided && (
                        <div className="flex gap-2 pt-2">
                          <button onClick={() => decideClaim(c, "rejected")} className="flex-1 text-xs font-bold text-[#B91C1C] border border-[#EF4444]/40 rounded-full px-3 py-1.5" data-testid={`claim-reject-${c.id}`}>Reject</button>
                          <button onClick={() => decideClaim(c, "approved")} className="flex-1 text-xs font-bold text-white bg-[#0a1f4d] rounded-full px-3 py-1.5" data-testid={`claim-approve-${c.id}`}>Approve</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "refunds" && (
          <div className="mt-6" data-testid="admin-refunds">
            <div className="mb-4 rounded-2xl border border-[#F59E0B]/40 bg-[#FFFBEB] p-4 text-sm text-[#78350F]">
              <p className="font-bold">Deposit refunds are processed manually.</p>
              <p className="mt-0.5">PayFast has no refund API — issue the refund in the PayFast merchant portal (or Stripe dashboard for test bookings), then mark it processed here. The customer is notified automatically.</p>
            </div>
            <Table headers={["Booking", "Customer", "Provider · ref", "Deposit", "Late fee", "Refund due", "Status", ""]}>
              {refunds.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-[#64748B]">No released deposits yet.</td></tr>
              )}
              {refunds.map((r) => (
                <tr key={r.id} className="border-t border-[#E2E8F0]" data-testid={`refund-row-${r.id}`}>
                  <td className="px-4 py-3 text-sm text-[#0a1f4d] max-w-[220px] truncate">{r.vehicle_title}</td>
                  <td className="px-4 py-3 text-sm text-[#334155]">{r.customer_name}</td>
                  <td className="px-4 py-3 text-xs text-[#64748B]"><span className="uppercase font-bold text-[#0a1f4d]">{r.payment_provider}</span>{r.provider_ref ? ` · ${String(r.provider_ref).slice(0, 18)}` : ""}</td>
                  <td className="px-4 py-3 text-sm text-[#0a1f4d]">{formatZAR(r.deposit)}</td>
                  <td className="px-4 py-3 text-sm text-[#B45309]">{r.late_fee ? `-${formatZAR(r.late_fee)}` : "—"}</td>
                  <td className="px-4 py-3 text-sm font-bold text-[#0a1f4d]">{formatZAR(r.refund)}</td>
                  <td className="px-4 py-3 text-xs"><Pill ok={r.refund_status === "processed"}>{r.refund_status === "held" ? "held · claim open" : r.refund_status}</Pill>{r.claim_deduction > 0 && <div className="text-[11px] text-[#B45309] mt-1">−{formatZAR(r.claim_deduction)} claim</div>}</td>
                  <td className="px-4 py-3 text-right">
                    {r.refund_status === "held" ? (
                      <button onClick={() => setTab("claims")} className="text-xs font-semibold text-[#B45309]" data-testid={`refund-held-${r.id}`}>Settle claim first</button>
                    ) : r.refund_status === "processed" ? (
                      <button onClick={() => markRefund(r.id, "pending")} className="text-xs font-semibold text-[#64748B] hover:text-[#0a1f4d]" data-testid={`refund-reopen-${r.id}`}>Reopen</button>
                    ) : (
                      <button onClick={() => markRefund(r.id, "processed")} className="text-xs font-bold text-white bg-[#0a1f4d] rounded-full px-3 py-1.5" data-testid={`refund-process-${r.id}`}>Mark processed</button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon }) => (
  <div className="rounded-2xl border border-[#E2E8F0] p-5 bg-white">
    <div className="text-[#1d7ffd]">{icon}</div>
    <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B] mt-3">{label}</p>
    <p className="font-display text-2xl font-black text-[#0a1f4d] mt-1">{value}</p>
  </div>
);

const Panel = ({ title, children }) => (
  <div className="rounded-2xl border border-[#E2E8F0] p-6 bg-white">
    <h3 className="font-display text-lg font-bold text-[#0a1f4d] mb-4">{title}</h3>
    {children}
  </div>
);

const Empty = ({ msg }) => <p className="text-sm text-[#64748B] py-4 text-center">{msg}</p>;

const Pill = ({ children, ok }) => (
  <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${ok ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#F0F2F5] text-[#475569]"}`}>{children}</span>
);

const StatusPill = ({ status }) => {
  const map = {
    confirmed: { bg: "bg-[#10B981]/15", text: "text-[#10B981]" },
    pending_payment: { bg: "bg-[#F59E0B]/15", text: "text-[#B45309]" },
    cancelled: { bg: "bg-[#EF4444]/15", text: "text-[#B91C1C]" },
  };
  const s = map[status] || { bg: "bg-[#F0F2F5]", text: "text-[#0a1f4d]" };
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${s.bg} ${s.text}`}>{status}</span>;
};

const Table = ({ headers, children }) => (
  <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-x-auto">
    <table className="w-full">
      <thead>
        <tr className="bg-[#F7F8FA]">
          {headers.map((h) => <th key={h} className="px-4 py-3 text-left text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B]">{h}</th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);
